import { lowerCase, startCase } from "lodash";
import moment from "moment-timezone";

import { StoreDetailsPageTranslations } from "~/bff/types/StoreDetailsPageTranslations";
import { StoreHours } from "~/bff/types/StoreHours";
import { StoreOpenInterval } from "~/bff/types/StoreOpenInterval";
import { StoreOpenIntervals } from "~/bff/types/StoreOpenIntervals";
import { isOpen } from "~/components/store-details-page/utils/is-open";
import { LOCALES } from "~/constants/i18n";
import { Nullable } from "~/types/general.types";

import { HoursType, Weekday } from "./types";

export const getLocalizedDaysText = (
  translations: StoreDetailsPageTranslations,
): {
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
} => ({
  sunday: translations.sundayText ?? "",
  monday: translations.mondayText ?? "",
  tuesday: translations.tuesdayText ?? "",
  wednesday: translations.wednesdayText ?? "",
  thursday: translations.thursdayText ?? "",
  friday: translations.fridayText ?? "",
  saturday: translations.saturdayText ?? "",
});

export const getOpenInterval = (openInterval?: StoreOpenInterval): string | null => {
  if (!openInterval) {
    return null;
  }

  return `${openInterval.start} - ${openInterval.end}`;
};

const getTranslatedDayName = ({
  day,
  translations,
}: {
  day: Weekday;
  translations: Nullable<StoreDetailsPageTranslations>;
}): string | null => translations?.[`${day}Text`] ?? day;

export const getNextOpenTime = ({
  now,
  hours,
  translations,
}: {
  now: moment.Moment;
  hours: StoreHours;
  translations: Nullable<StoreDetailsPageTranslations> | undefined;
}): string => {
  const currentWeekDay = lowerCase(moment().locale("en").format("dddd"));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filteredHours: [keyof typeof hours, any][] = Object.entries(hours).filter(
    (item) => item[0] !== "__typename",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as [keyof typeof hours, any][];

  const splitAndConcatHours = filteredHours
    .splice(filteredHours.findIndex((value) => value[0] === currentWeekDay))
    .concat(filteredHours);

  const nextOpenDay = splitAndConcatHours.find(
    ([day, { isClosed, openIntervals }]) => {
      // IF today
      if (!isClosed && day === currentWeekDay) {
        const start = moment(now);
        start.hours(+openIntervals[0].start.split(":")[0]);
        start.minutes(+openIntervals[0].start.split(":")[1]);
        return now.isBefore(start);
      }
      // IF other day
      return !isClosed;
    },
  );

  if (!nextOpenDay) {
    return "";
  }

  const opensToday = nextOpenDay[0] === currentWeekDay;

  const opensTomorrow =
    nextOpenDay[0] === lowerCase(moment(now).add(1, "days").format("dddd"));

  const translatedDayName = getTranslatedDayName({
    day: nextOpenDay[0],
    translations: translations ?? null,
  });

  return `${translations?.hoursDescriptionOpensAt} ${
    nextOpenDay[1].openIntervals[0].start
  }${opensToday || opensTomorrow ? "" : ` ${startCase(translatedDayName ?? undefined)}`}`;
};

export const getLocalizedDay = (date: string, locale: LOCALES) => {
  const formatL = moment.localeData(locale).longDateFormat("L");
  const formatYearlessL = formatL.replace(/Y/g, "");
  const localizedDay = moment(date)
    .locale?.(locale)
    .format?.(formatYearlessL)
    .replace?.(/\/$|(. )$|^\.|\.$|-$/g, "");

  return localizedDay;
};

type StoreSchedule =
  | Array<{
      day: string;
      hours: string | null;
      current: boolean;
    }>
  | undefined;

interface GetHolidaysScheduleProps {
  now: moment.Moment;
  hours: HoursType;
  timeZone?: string;
  translations?: Nullable<StoreDetailsPageTranslations> | undefined;
  regularSchedule?: HoursType;
  locale: LOCALES;
}
export const getHolidaysSchedule = ({
  now,
  hours,
  timeZone,
  translations,
  regularSchedule,
  locale,
}: GetHolidaysScheduleProps): StoreSchedule => {
  const { holidayHoursClosedText } = translations ?? {};

  const isRegularScheduleAsStoreOpenIntervals = Array.isArray(regularSchedule);

  const nearestHolidayIndex =
    typeof timeZone === "string"
      ? (hours as StoreOpenIntervals[])?.findIndex(({ date }) =>
          moment(date).tz(timeZone, true).isSameOrAfter(now, "day"),
        )
      : -1;

  return (hours as StoreOpenIntervals[])
    ?.slice(nearestHolidayIndex, nearestHolidayIndex + 7)
    .map(({ date, isClosed, openIntervals }) => {
      const targetDay = moment(date).locale("en-us");

      const isClosedOnTargetDay =
        isClosed ||
        (!openIntervals &&
          !isRegularScheduleAsStoreOpenIntervals &&
          !regularSchedule?.[targetDay.format("dddd").toLowerCase() as Weekday]
            ?.openIntervals);

      return {
        day: getLocalizedDay(date ?? "", locale),
        hours:
          (isClosedOnTargetDay
            ? holidayHoursClosedText
            : getOpenInterval(
                (openIntervals
                  ? openIntervals[0]
                  : !isRegularScheduleAsStoreOpenIntervals &&
                      regularSchedule?.[
                        targetDay.format("dddd").toLowerCase() as Weekday
                      ]?.openIntervals
                    ? !isRegularScheduleAsStoreOpenIntervals &&
                      regularSchedule?.[
                        targetDay.format("dddd").toLowerCase() as Weekday
                      ]?.openIntervals?.[0]
                    : undefined) ?? undefined,
              )) ?? null,
        current:
          typeof timeZone === "string"
            ? moment(date).tz(timeZone, true).isSame(now, "day")
            : moment(date).isSame(now, "day"),
      };
    });
};

interface GetCommonScheduleProps {
  now: moment.Moment;
  hours?: HoursType;
  translations?: Nullable<StoreDetailsPageTranslations> | undefined;
}
export const getCommonSchedule = ({
  hours,
  translations,
}: GetCommonScheduleProps): StoreSchedule => {
  const localizedDaysText = getLocalizedDaysText(
    translations as StoreDetailsPageTranslations,
  );
  const currentWeekDay = moment().locale("en").format("dddd");

  const data = Object.entries(hours ?? {})
    .filter((item) => item[0] !== "__typename")
    .map(([day, { isClosed, openIntervals }]) => ({
      day: localizedDaysText[day as keyof typeof hours],
      hours: isClosed
        ? translations?.holidayHoursClosedText || "-"
        : getOpenInterval(openIntervals[0]),
      current: startCase(day) === currentWeekDay,
    }));

  return data.splice(data.findIndex((hours) => hours.current)).concat(data);
};

interface GetCurrentLabelProps {
  isHolidayHours?: boolean;
  now: moment.Moment;
  hours?: HoursType;
  translations?: Nullable<StoreDetailsPageTranslations> | undefined;
}
export const getCurrentDayLabel = ({
  isHolidayHours,
  now,
  hours,
  translations,
}: GetCurrentLabelProps): string | null => {
  const { upcomingHolidaysText, openNowText, closedNowText } = translations ?? {};

  const isHoursAsStoreOpenIntervals = Array.isArray(hours);

  if (isHolidayHours) {
    return upcomingHolidaysText ?? null;
  }

  const currentWeekDay = moment()
    .locale("en")
    .format("dddd")
    .toLowerCase() as Weekday;

  if (
    isOpen(
      now,
      (!isHoursAsStoreOpenIntervals && hours?.[currentWeekDay]) || undefined,
    )
  ) {
    return openNowText ?? null;
  }

  return closedNowText ?? null;
};

export const getCurrentDayData = ({
  isHolidayHours,
  now,
  hours,
  schedule,
  translations,
}: {
  isHolidayHours?: boolean;
  now: moment.Moment;
  hours?: HoursType;
  schedule: StoreSchedule;
  translations: Nullable<StoreDetailsPageTranslations> | undefined;
}): string => {
  if (isHolidayHours) {
    return `${schedule?.[0]?.day}, ${schedule?.[0]?.hours}`;
  }

  const isHoursAsStoreOpenIntervals = Array.isArray(hours);

  const currentWeekDay = moment()
    .locale("en")
    .format("dddd")
    .toLowerCase() as Weekday;

  const isOpenNow = isOpen(
    now,
    (!isHoursAsStoreOpenIntervals && hours?.[currentWeekDay]) || undefined,
  );
  const willBeOpenNext = getNextOpenTime({
    now,
    hours: hours as StoreHours,
    translations,
  });
  return isOpenNow
    ? schedule?.find((hours) => hours.current)?.hours ?? ""
    : willBeOpenNext;
};
